import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AuthenticationDetails, CognitoUser, CognitoUserSession,
  CognitoIdToken, CognitoAccessToken, CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { userPool, SKIP_LOGIN, COGNITO_HOSTED_DOMAIN } from './cognito';
import { api } from './api';

export type AuthView = 'landing' | 'signin' | 'signup';
export type FederatedProvider = 'Google';

// Roles mirror the backend (auth.py): everyone is 'unpaid' by default, 'admin'
// is an email-whitelist grant (and always wins), 'paid' is reserved for a future
// payment flow. The effective role is computed server-side and returned on the
// user summary, so the client just reads it.
export type Role = 'admin' | 'paid' | 'unpaid';

// `flash` carries a one-shot notice into the signin screen, primarily for the
// "your session expired, please sign in again" case. Consumed (cleared) by the
// SignIn screen the first time it reads it.
export type AuthState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated'; view: AuthView; flash?: string }
  | { kind: 'unverified'; email: string; password?: string }
  | { kind: 'needs_handle'; idToken: string; email: string }
  | { kind: 'authenticated'; idToken: string; email: string; user: { handle: string; role: Role } };

interface AuthContextValue {
  state: AuthState;
  showLanding: () => void;
  showSignIn: () => void;
  showSignUp: () => void;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  federatedSignIn: (provider: FederatedProvider) => void;
  consumeOAuthCode: (code: string) => Promise<void>;
  confirmEmail: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  submitHandle: (handle: string) => Promise<void>;
  signOut: () => void;
  /** Consume the one-shot flash message (clears it from state). */
  consumeFlash: () => string | undefined;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function devStubAuthenticated(): AuthState {
  return {
    kind: 'authenticated',
    idToken: '',
    email: 'dev@local.test',
    // LOCAL_DEV is admin (matches backend is_admin) so local dev gets the full
    // edit surface without a real whitelisted login.
    user: { handle: 'admin', role: 'admin' },
  };
}

// Normalize whatever the API returns into a known Role; default to 'unpaid'.
function toRole(value: unknown): Role {
  return value === 'admin' || value === 'paid' ? value : 'unpaid';
}

async function fetchUserSummary(idToken: string, email: string): Promise<AuthState> {
  const res = await api('/api/users/me', { token: idToken });
  if (res.status === 404) return { kind: 'needs_handle', idToken, email };
  if (!res.ok) return { kind: 'unauthenticated', view: 'landing' };
  const user = await res.json();
  return {
    kind: 'authenticated', idToken, email,
    user: { handle: user.handle, role: toRole(user.role) },
  };
}

function sessionToState(session: CognitoUserSession): Promise<AuthState> {
  const idToken = session.getIdToken();
  const email = (idToken.payload.email as string) || '';
  // Federated providers verify the email themselves — skip the verification gate.
  const identities = idToken.payload.identities;
  const isFederated = Array.isArray(identities) && identities.length > 0;
  const verified = !!idToken.payload.email_verified || isFederated;
  if (!verified) return Promise.resolve<AuthState>({ kind: 'unverified', email });
  return fetchUserSummary(idToken.getJwtToken(), email);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });
  const lastEmail = useRef<string>('');

  const resolveSession = useCallback(async () => {
    if (SKIP_LOGIN || !userPool) {
      setState(devStubAuthenticated());
      return;
    }
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setState({ kind: 'unauthenticated', view: 'landing' });
      return;
    }
    // `getCurrentUser()` returns a non-null user when there's a persisted
    // session in localStorage — even if it's now expired and unrefreshable.
    // That's the case we want to flag: the user thinks they're signed in
    // and the app is about to silently kick them. Show a flash so they
    // know what just happened.
    cognitoUser.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
      try {
        if (err || !session?.isValid()) {
          setState({
            kind: 'unauthenticated',
            view: 'signin',
            flash: 'your session expired — please sign in again.',
          });
          return;
        }
        setState(await sessionToState(session));
      } catch (e) {
        // sessionToState can throw if /api/users/me errors out unexpectedly
        // or json() fails. Don't leave the app stuck in `loading`.
        // eslint-disable-next-line no-console
        console.error('Auth session resolve failed:', e);
        setState({ kind: 'unauthenticated', view: 'landing' });
      }
    });
  }, []);

  useEffect(() => { void resolveSession(); }, [resolveSession]);

  const showLanding = useCallback(() => setState({ kind: 'unauthenticated', view: 'landing' }), []);
  const showSignIn = useCallback(() => setState({ kind: 'unauthenticated', view: 'signin' }), []);
  const showSignUp = useCallback(() => setState({ kind: 'unauthenticated', view: 'signup' }), []);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!userPool) throw new Error('Cognito not configured');
    const pool = userPool;
    lastEmail.current = email;
    await new Promise<void>((resolve, reject) => {
      pool.signUp(email, password, [], [], (err) => {
        if (err) reject(err); else resolve();
      });
    });
    setState({ kind: 'unverified', email, password });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!userPool) throw new Error('Cognito not configured');
    lastEmail.current = email;
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    await new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: async (session) => {
          setState(await sessionToState(session));
          resolve();
        },
        onFailure: (err) => {
          if (err.code === 'UserNotConfirmedException') {
            setState({ kind: 'unverified', email, password });
            resolve();
          } else {
            reject(err);
          }
        },
      });
    });
  }, []);

  const federatedSignIn = useCallback((provider: FederatedProvider) => {
    if (!userPool || !COGNITO_HOSTED_DOMAIN) {
      throw new Error('OAuth not configured');
    }
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const authUrl = new URL(`https://${COGNITO_HOSTED_DOMAIN}/oauth2/authorize`);
    authUrl.searchParams.set('client_id', userPool.getClientId());
    authUrl.searchParams.set('redirect_uri', redirectUri);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'email openid profile');
    authUrl.searchParams.set('identity_provider', provider);
    window.location.href = authUrl.toString();
  }, []);

  const consumeOAuthCode = useCallback(async (code: string) => {
    if (!userPool || !COGNITO_HOSTED_DOMAIN) throw new Error('OAuth not configured');
    const pool = userPool;
    const redirectUri = `${window.location.origin}/oauth/callback`;
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: pool.getClientId(),
      code,
      redirect_uri: redirectUri,
    });
    const res = await fetch(`https://${COGNITO_HOSTED_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Token exchange failed: ${text}`);
    }
    const tokens = await res.json();
    const idToken = new CognitoIdToken({ IdToken: tokens.id_token });
    const accessToken = new CognitoAccessToken({ AccessToken: tokens.access_token });
    const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token });
    const session = new CognitoUserSession({
      IdToken: idToken,
      AccessToken: accessToken,
      RefreshToken: refreshToken,
    });
    const sub = idToken.payload.sub as string;
    const cognitoUser = new CognitoUser({ Username: sub, Pool: pool });
    cognitoUser.setSignInUserSession(session);
    setState(await sessionToState(session));
  }, []);

  const confirmEmail = useCallback(async (code: string) => {
    if (state.kind !== 'unverified') return;
    if (!userPool) throw new Error('Cognito not configured');
    const { email, password } = state;
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    await new Promise<void>((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) reject(err); else resolve();
      });
    });
    // Drop the password from React state as soon as we've used it, so a later
    // XSS or memory dump can't lift it from the state tree.
    if (password) {
      try {
        await signIn(email, password);
      } finally {
        setState((s) => (s.kind === 'unverified' ? { ...s, password: undefined } : s));
      }
    } else {
      setState({ kind: 'unauthenticated', view: 'signin' });
    }
  }, [state, signIn]);

  const resendCode = useCallback(async () => {
    if (state.kind !== 'unverified') return;
    if (!userPool) throw new Error('Cognito not configured');
    const cognitoUser = new CognitoUser({ Username: state.email, Pool: userPool });
    await new Promise<void>((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err) => {
        if (err) reject(err); else resolve();
      });
    });
  }, [state]);

  const submitHandle = useCallback(async (handle: string) => {
    if (state.kind !== 'needs_handle') return;
    const res = await api('/api/users/init', {
      method: 'POST',
      token: state.idToken,
      body: { handle },
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => ({}));
      throw new Error(detail.detail || `init failed (${res.status})`);
    }
    const user = await res.json();
    setState({
      kind: 'authenticated',
      idToken: state.idToken,
      email: state.email,
      user: { handle: user.handle, role: toRole(user.role) },
    });
  }, [state]);

  const signOut = useCallback(() => {
    userPool?.getCurrentUser()?.signOut();
    setState({ kind: 'unauthenticated', view: 'landing' });
  }, []);

  // One-shot flash read. SignIn calls this on mount; clearing in state
  // prevents the message from re-appearing on subsequent renders.
  const consumeFlash = useCallback((): string | undefined => {
    if (state.kind !== 'unauthenticated' || !state.flash) return undefined;
    const msg = state.flash;
    setState({ kind: 'unauthenticated', view: state.view });
    return msg;
  }, [state]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state, showLanding, showSignIn, showSignUp, signUp, signIn,
      federatedSignIn, consumeOAuthCode,
      confirmEmail, resendCode, submitHandle, signOut,
      consumeFlash,
    }),
    [state, showLanding, showSignIn, showSignUp, signUp, signIn,
     federatedSignIn, consumeOAuthCode,
     confirmEmail, resendCode, submitHandle, signOut, consumeFlash],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Effective role of the signed-in user, or 'unpaid' when not authenticated. */
export function useRole(): Role {
  const { state } = useAuth();
  return state.kind === 'authenticated' ? state.user.role : 'unpaid';
}

/** True only for admins — gates all author/edit affordances. */
export function useIsAdmin(): boolean {
  return useRole() === 'admin';
}

/** Future paid-gating: admins and paid users pass; everyone else is gated.
 * Wired now so blog/problem content can be paid-gated later without a refactor. */
export function gatePaid(role: Role): boolean {
  return role === 'admin' || role === 'paid';
}
